import { useCallback } from 'react';
import { StyleSheet } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useBoolean } from 'usehooks-ts';
import {
	Toast,
	Button,
	CheckBox,
	DefaultLayout,
	LoadingOverlay,
	Text,
	showErrorToUser,
	colors,
	spacing,
} from '@pletly/ui';

import { setCallbackUrl } from '@pletly/feature-auth';
import { RootStackScreenProps } from '../../../../navigation/types';
import { useCurrentTermsOfUseQuery } from '../new/components/__generated__/currentTerms.generated';
import { useAcceptTermsOfUseMutation } from './components/__generated__/acceptTerms.generated';
import { withTranslation } from '../../../../locales/hoc';

type Props = RootStackScreenProps<'AcceptTermsOfUse'>;

export const AcceptTermsPage = withTranslation(
	({
		route: {
			params: { uuid: organisationUuid },
		},
	}: Props) => {
		const { t } = useTranslation();
		const { data, loading } = useCurrentTermsOfUseQuery({ variables: { organisationUuid } });
		const [acceptTerms, { loading: isAccepting }] = useAcceptTermsOfUseMutation();
		const { value: isAccepted, setValue: setChecked } = useBoolean();

		const termsId = data?.organisation.termsOfUse?.id;

		const handleAccept = useCallback(async () => {
			if (isAccepting) return;

			if (!termsId) return;

			try {
				await acceptTerms({
					variables: {
						id: termsId,
					},
				});
				await setCallbackUrl(undefined);

				Toast.show({
					type: 'success',
					text1: t('terms-of-use.terms-of-use-accepted'),
				});

				return window.location.replace('/');
			} catch (error) {
				showErrorToUser(error as Error);
			}
		}, [t, acceptTerms, termsId, isAccepting]);

		if (loading) return <LoadingOverlay show />;

		if (!data?.organisation.termsOfUse) {
			return null;
		}

		return (
			<DefaultLayout view>
				<Text category="h1">{t('terms-of-use.title')}</Text>
				<Text style={styles.termsText}>{data.organisation.termsOfUse.text}</Text>
				<CheckBox disabled={isAccepting} onChange={setChecked} checked={isAccepted}>
					<Text>
						{t('terms-of-use.i-agree-to-the-terms-of-use-as-outlined-by')} {data.organisation.name}
					</Text>
				</CheckBox>

				<Button
					color="primary"
					variant="filled"
					disabled={isAccepting || !isAccepted}
					onPress={handleAccept}
				>
					{t('terms-of-use.accept')}
				</Button>
			</DefaultLayout>
		);
	},
);

const styles = StyleSheet.create({
	termsText: {
		backgroundColor: colors.white,
		padding: spacing(2),
		borderRadius: 8,
	},
});
